import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { usePoll } from "../lib/usePoll";
import { Btn, cn } from "../components/ui/kit";
import { clock } from "../lib/format";

export default function SuperAdminApp() {
  const { data: cfg, refetch } = usePoll(() => api.get("/config").then((r) => r.data), 0);
  const { data: users } = usePoll(() => api.get("/auth/users").then((r) => r.data), 0);
  const { data: att, refetch: refetchAtt } = usePoll(() => api.get("/attendance").then((r) => r.data), 5000);
  const [pin, setPin] = useState("");

  if (!cfg) return <div className="grid min-h-[calc(100vh-49px)] place-items-center bg-zinc-900"><div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" /></div>;

  const toggles = cfg.feature_toggles || {};
  const patch = async (p) => { try { await api.patch("/config", { patch: p }); toast.success("Config saved"); refetch(); } catch (e) { toast.error(apiError(e)); } };
  const setToggle = (k) => patch({ feature_toggles: { ...toggles, [k]: !toggles[k] } });

  const reset = async () => {
    if (!window.confirm("Reset sandbox to preloaded demo state? This clears all orders, payments and inventory movements.")) return;
    try { await api.post("/admin/sandbox-reset"); toast.success("Sandbox reset complete"); refetch(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const attendance = async (action) => {
    try { const { data } = await api.post("/attendance", { pin, action }); toast.success(`${data.employee} timed ${action}${data.hours ? ` · ${data.hours}h` : ""}`); setPin(""); refetchAtt(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-[calc(100vh-49px)] bg-zinc-900 text-zinc-100">
      <div className="mx-auto max-w-[1400px] space-y-4 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-head text-2xl font-black">System Control Center</h1>
            <p className="text-sm text-violet-300">Super Admin · configuration, feature toggles & sandbox controls</p>
          </div>
          <Btn variant="danger" data-testid="sandbox-reset-btn" onClick={reset}>↻ Reset Sandbox</Btn>
        </div>

        <Card title="Feature Toggles">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {[
              ["offline_mode", "Offline Mode (degraded)"],
              ["auto_chime", "Kitchen Auto-Chime"],
              ["loyalty", "Loyalty & Rewards"],
              ["analytics", "Analytics Events"],
              ["online_ordering", "Online Ordering"],
            ].map(([k, label]) => (
              <button key={k} data-testid={`toggle-${k}`} onClick={() => setToggle(k)}
                className={cn("flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-bold", toggles[k] ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-zinc-700 text-zinc-400")}>
                {label}
                <span className={cn("ml-3 h-5 w-9 rounded-full p-0.5 transition", toggles[k] ? "bg-emerald-500" : "bg-zinc-700")}>
                  <span className={cn("block h-4 w-4 rounded-full bg-white transition", toggles[k] && "translate-x-4")} />
                </span>
              </button>
            ))}
          </div>
          {toggles.offline_mode && <p className="mt-2 text-xs text-rose-300">⚠ Offline: only Cash / COD accepted. Orders queue as PENDING SYNC until connection returns.</p>}
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="Business Configuration">
            <Num label="Kitchen GREEN max (min)" value={cfg.kitchen_timer.green_max_min} onSave={(v) => patch({ kitchen_timer: { ...cfg.kitchen_timer, green_max_min: v } })} />
            <Num label="Kitchen YELLOW max (min)" value={cfg.kitchen_timer.yellow_max_min} onSave={(v) => patch({ kitchen_timer: { ...cfg.kitchen_timer, yellow_max_min: v } })} />
            <Num label="Service Charge rate (0-1)" value={cfg.service_charge.rate} step="0.01" onSave={(v) => patch({ service_charge: { ...cfg.service_charge, rate: v } })} />
            <Num label="Senior/PWD discount rate (0-1)" value={cfg.senior_pwd.discount_rate} step="0.01" onSave={(v) => patch({ senior_pwd: { ...cfg.senior_pwd, discount_rate: v } })} />
            <Num label="VAT rate (0-1)" value={cfg.tax.vat_rate} step="0.01" onSave={(v) => patch({ tax: { ...cfg.tax, vat_rate: v } })} />
          </Card>

          <div className="space-y-4">
            <Card title="Staff Attendance (PIN)">
              <div className="flex gap-2">
                <input value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Employee PIN" data-testid="att-pin"
                  className="flex-1 rounded-xl border border-zinc-700 bg-zinc-800 px-3 py-2 outline-none" />
                <Btn variant="emerald" data-testid="att-in" onClick={() => attendance("in")}>Time In</Btn>
                <Btn variant="outline" className="text-zinc-100" data-testid="att-out" onClick={() => attendance("out")}>Time Out</Btn>
              </div>
              <div className="mt-3 max-h-40 overflow-auto text-xs">
                {(att || []).map((a) => (
                  <div key={a.id} className="flex justify-between border-t border-zinc-800 py-1 first:border-0">
                    <span>{a.employee} <span className="text-zinc-500">({a.role})</span></span>
                    <span className="font-mono text-zinc-400">{clock(a.time_in)}→{a.time_out ? clock(a.time_out) : "…"} {a.hours ? `· ${a.hours}h` : ""}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card title="Users & Roles">
              <div className="max-h-40 overflow-auto text-sm">
                {(users || []).map((u) => (
                  <div key={u.user_id} className="flex justify-between border-t border-zinc-800 py-1 first:border-0">
                    <span>{u.name} <span className="text-zinc-500">{u.email}</span></span>
                    <span className="rounded-full bg-violet-500/20 px-2 py-0.5 text-xs font-bold text-violet-300">{u.role}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
        <p className="text-center text-xs text-zinc-600">RestoCore Sandbox v0.1 · Systems Owner-Architect Michael Carlos · CTO Erick Kalugdan</p>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return <div className="rounded-2xl border border-zinc-800 bg-zinc-950/50 p-4"><h3 className="mb-3 font-head text-sm font-black uppercase tracking-wide text-zinc-500">{title}</h3>{children}</div>;
}
function Num({ label, value, step = "1", onSave }) {
  return (
    <div className="flex items-center justify-between border-t border-zinc-800 py-2 first:border-0">
      <span className="text-sm text-zinc-300">{label}</span>
      <input type="number" step={step} defaultValue={value} data-testid={`cfg-${label.replace(/[^a-z]/gi, "").toLowerCase()}`}
        onBlur={(e) => Number(e.target.value) !== value && onSave(Number(e.target.value))}
        className="w-24 rounded-lg border border-zinc-700 bg-zinc-800 px-2 py-1 text-right font-mono" />
    </div>
  );
}
